"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.alterPolyTraversalDirection = alterPolyTraversalDirection;
exports.checkDuplicateReferences = checkDuplicateReferences;
exports.computeBboxs = computeBboxs;
exports.convexDecomposition = convexDecomposition;
exports.eliminateHoles = eliminateHoles;
exports.getAngleBisector = getAngleBisector;
exports.getPolyArea = getPolyArea;
exports.markReflexPoints = markReflexPoints;
exports.rayCast = rayCast;
exports.removeCollinearPoints = removeCollinearPoints;
exports.removeDuplicatePoints = removeDuplicatePoints;
var _vector = require("./vector");
/**
 * Compute the convex decomposition of a polygon, which may or may not contain holes, using the angle-bisector method.
 * @param poly A closed, simple polygon. 
 * @param holes An array of closed, simple, non-intersecting polygons. These are all holes.
 * @returns An array of convex polygons that exactly cover the original polygon.
 */
function convexDecomposition(poly, holes = []) {
  if (poly.length === 0) {
    return [];
  }
  if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
    checkDuplicateReferences(poly, ...holes);
  }

  // Copy arrays, remove duplicate points, and standardize the traversal direction
  poly = poly.slice();
  removeDuplicatePoints(poly);
  removeCollinearPoints(poly);

  // If all points were duplicates or collinear, there will be none left. (Technically, there should be 1 point left,
  // but due to a bug, I mean feature, in removeDuplicatePoints, a single point is considered a duplicate of itself.)
  if (poly.length === 0) return [];
  alterPolyTraversalDirection(poly, 'ccw');
  holes = holes.map(hole => hole.slice());
  holes.forEach(hole => removeDuplicatePoints(hole));
  holes.forEach(hole => removeCollinearPoints(hole));
  holes.forEach(hole => alterPolyTraversalDirection(hole, 'cw'));
  if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
    checkDuplicateReferences(poly, ...holes);
  }
  let originalArea;
  if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
    let bbox = {};
    poly.forEach(p => {
      bbox.min = (0, _vector.min)(bbox.min, p);
      bbox.max = (0, _vector.max)(bbox.max, p);
    });
    if (!(0, _vector.isBboxDefined)(bbox)) {
      throw new Error('Could not compute bounding box for polygon');
    }
    setSvgOffset({
      x: 0,
      y: (bbox.max.y - bbox.min.y) * 1.1
    });
    debugSvg([poly], holes);
    originalArea = Math.abs(getPolyArea(poly)) - holes.reduce((a, c) => a + Math.abs(getPolyArea(c)), 0);
  }

  // Eliminate holes
  eliminateHoles(poly, holes);
  if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
    checkDuplicateReferences(poly);
  }
  if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
    debugSvg([poly]);
  }

  // Find reflex points
  let numberOfReflexPoints = markReflexPoints(poly, 'ccw');

  // If there are no reflex points, the polygon is convex.
  if (numberOfReflexPoints === 0) {
    return [poly];
  }
  if ((process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') && originalArea) {
    // Check area. It should be identical to the original poly minus the holes.
    let currentArea = getPolyArea(poly);
    if (Math.abs((currentArea - originalArea) / originalArea) > 1e-12) {
      saveSvg();
      throw new Error(`Error: Polygon area was not conserved while eliminating holes. ${currentArea} !== ${originalArea}`);
    }
  }
  let convexPolygons = [];
  let concavePolygons = [poly];
  while (concavePolygons.length > 0) {
    // Find a reflex point on the first of the concave polygons. If the first reflex point fails the rayCast for some
    // reason, we'll keep trying.
    let concavePolygon = concavePolygons[0];
    let index1 = -1;
    let point = null;
    let direction = null;
    let rayCastResult = null;
    for (let i = 0; i < concavePolygon.length; i++) {
      if (concavePolygon[i].reflex) {
        // Get the reflex point's angle bisector
        ;
        ({
          point,
          direction
        } = getAngleBisector(concavePolygon, i));

        // Find the other side of the bisector
        rayCastResult = rayCast(concavePolygon, point, direction, 'ccw', i);
        if (rayCastResult) {
          index1 = i;
          break;
        } else {
          // Hmm, rayCast normally works. Output some troubleshooting information.
          let {
            point: _point,
            direction: _direction
          } = getAngleBisector(concavePolygon, i);
          if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
            debugSvg([concavePolygon], [[point, (0, _vector.add)(point, (0, _vector.mul)(direction, 1))]]);
            saveSvg();
          }
          console.error(`Error: Unable to find the other side of the bisector.`);
          console.error({
            concavePolygon,
            i,
            point,
            direction
          });
        }
      }
    }
    if (index1 === -1 || !rayCastResult || !point || !direction) {
      // No more reflex points, or could not find a reflex point that worked.
      convexPolygons.push(concavePolygon);
      concavePolygons.shift();
      continue;
    }
    let {
      index: index2,
      t,
      point: oppositePoint
    } = rayCastResult;
    let isOppositeReflex = concavePolygon[index2].reflex;
    // Insert new point if necessary
    if (t !== 0) {
      concavePolygon.splice(index2 + 1, 0, {
        ...oppositePoint
      }); // The new point will not be a reflex point
      // Adjust indexes
      if (index1 > index2) {
        index1++;
      }
      index2++;
    }

    // This point is no longer a reflex point
    delete concavePolygon[index1].reflex;

    // Order indexes
    if (index1 > index2) {
      let temp = index1;
      index1 = index2;
      index2 = temp;
    }

    // Split the concave polygon into two polygons at reflexPointIndex and oppositePointIndex
    let polygon1 = concavePolygon.slice(0, index1 + 1).concat(concavePolygon.slice(index2));
    let polygon2 = concavePolygon.slice(index1, index2 + 1);
    // Clone the points that were duplicated on one of the polygons
    polygon1[index1] = {
      ...polygon1[index1]
    };
    polygon1[index1 + 1] = {
      ...polygon1[index1 + 1]
    };
    let polygon1Area = Math.abs(getPolyArea(polygon1));
    let polygon2Area = Math.abs(getPolyArea(polygon2));
    if (polygon1Area < 1e-12 || polygon2Area < 1e-12) {
      // Splitting resulted in at least one of the new polygons having zero area. Abort splitting this polygon, but
      // leave the reflex point unmarked.
      continue;
    }
    let newPolygons = [polygon1, polygon2];
    if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
      // Check for duplicate references
      checkDuplicateReferences(...newPolygons);
    }
    if (t === 0 && isOppositeReflex) {
      // It's possible that the opposite points are also no longer reflex points
      newPolygons.forEach(polygon => {
        markReflexPoints(polygon, 'ccw');
      });
    }
    if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
      // For testing, check that the reflex points are still correct

      for (let polygon of newPolygons) {
        let numberOfReflexPointsBefore = polygon.reduce((a, c) => a + (c.reflex ? 1 : 0), 0);
        let numberOfReflexPointsAfter = markReflexPoints(polygon, 'ccw');
        if (numberOfReflexPointsBefore !== numberOfReflexPointsAfter) {
          resetSvg();
          debugSvg([concavePolygon], [polygon]);
          saveSvg();
          console.log(concavePolygon);
          console.log(polygon);
          console.log(rayCastResult);
          throw new Error(`Error: The reflex points were not correctly marked after splitting the polygon. ${numberOfReflexPointsBefore} !== ${numberOfReflexPointsAfter}`);
        }
      }
    }

    // Add the new polygons
    concavePolygons.shift();
    concavePolygons.push(...newPolygons);
  }
  if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
    checkDuplicateReferences(...convexPolygons);
  }
  return convexPolygons;
}

/**
 * Remove duplicate points from a polygon, modifying the original array.
 * @param poly The polygon
 * @param tolerance The tolerance for considering two points to be the same
 */
function removeDuplicatePoints(poly, tolerance = 1e-8) {
  let tolerance2 = tolerance * tolerance;
  while (true) {
    let nPointsRemoved = 0;
    for (let i = 0; i < poly.length; i++) {
      let ip1 = (i + 1) % poly.length;
      let p1 = poly[i];
      let p2 = poly[ip1];
      if ((0, _vector.norm2)((0, _vector.sub)(p1, p2)) < tolerance2) {
        let pMid = (0, _vector.mul)((0, _vector.add)(p1, p2), 0.5);
        poly.splice(i, 1, pMid);
        poly.splice(ip1, 1);
        nPointsRemoved++;
      }
    }
    if (nPointsRemoved === 0) break;
  }
  return poly;
}

/**
 * Remove collinear points from a polygon, modifying the original array.
 * @param poly The polygon
 * @param tolerance The tolerance for considering two points to be the same
 */
function removeCollinearPoints(poly, tolerance = 1e-8) {
  let tolerance2 = tolerance * tolerance;
  while (true) {
    let nPointsRemoved = 0;
    for (let i = 0; i < poly.length; i++) {
      let ip1 = (i + 1) % poly.length;
      let ip2 = (i + 2) % poly.length;
      let p1 = poly[i];
      let p2 = poly[ip1];
      let p3 = poly[ip2];
      let p12 = (0, _vector.sub)(p2, p1);
      let p23 = (0, _vector.sub)(p3, p2);
      let crossProd = (0, _vector.cross)(p12, p23);
      // Normalize the test without taking a square root
      let normp12 = (0, _vector.norm2)(p12);
      let normp23 = (0, _vector.norm2)(p23);
      if (Math.abs(crossProd * crossProd / (normp12 * normp23)) < tolerance2) {
        poly.splice(ip1, 1);
        i--;
        nPointsRemoved++;
      }
    }
    if (nPointsRemoved === 0) break;
  }
  return poly;
}
function computeBboxs(holes) {
  // Calculate bounding box for each hole
  for (let hole of holes) {
    let bbox = {};
    hole.forEach(p => {
      bbox.min = (0, _vector.min)(bbox.min, p);
      bbox.max = (0, _vector.max)(bbox.max, p);
    });
    if (!(0, _vector.isBboxDefined)(bbox)) throw new Error('Error: Could not compute bounding box for hole');
    hole.bbox = bbox;
  }
}

/**
 * Eliminates holes by joining them to the outer polygon with narrow (0-width) channels. The outer polygon is modified in place.
 * @param {Vector[]} poly A closed, simple polygon.
 * @param {Vector[][]} holes An array of closed, simple, non-intersecting polygons representing the holes that will be eliminated.
 */
function eliminateHoles(poly, holes) {
  let holesWithBbox = holes;
  computeBboxs(holesWithBbox);

  // Eliminate holes by casting rays upward from holes to intersect the outer poly, then joining
  // the hole into the outer poly. By beginning with the uppermost point on the uppermost hole, its angle bisector will
  // point upward, which is guaranteed to intersect the outer poly rather than another hole.

  while (holesWithBbox.length > 0) {
    // Find the hole with the greatest y-coordinate
    let maxY = -Infinity;
    let holeIndex = -1;
    for (let i = 0; i < holesWithBbox.length; i++) {
      let bbox = holesWithBbox[i].bbox;
      if (!(0, _vector.isBboxDefined)(bbox)) throw new Error('Error: Hole does not have a bounding box');
      if (bbox.max.y > maxY) {
        maxY = bbox.max.y;
        holeIndex = i;
      }
    }

    // Find the point on this hole with the greatest y-coordinate
    let hole = holesWithBbox[holeIndex];
    let maxYPoint = -Infinity;
    let pointIndex = -1;
    for (let i = 0; i < hole.length; i++) {
      if (hole[i].y > maxYPoint) {
        maxYPoint = hole[i].y;
        pointIndex = i;
      }
    }
    let ray = getAngleBisector(hole, pointIndex);

    // Intersect the ray with the poly
    let intersection = rayCast(poly, ray.point, ray.direction, 'ccw');
    if (!intersection) {
      if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
        debugSvg([poly], [[ray.point, (0, _vector.add)(ray.point, (0, _vector.mul)(ray.direction, 1))]]);
      }
      console.error('Angle bisector of hole did not intersect outer polygon. Skipping this hole.');
      holesWithBbox.splice(holeIndex, 1);
      continue;
    }

    // Join the hole into the outer poly
    // Re-index the hole so that it begins and ends at the point index (the point is duplicated)
    let reIndexedHole = hole.slice(pointIndex).concat(hole.slice(0, pointIndex + 1));
    reIndexedHole[0] = {
      ...reIndexedHole[0]
    };
    if (intersection.t !== 0) {
      // Add the intersection points.
      reIndexedHole = [{
        ...intersection.point
      }, ...reIndexedHole, {
        ...intersection.point
      }];
    } else {
      // The intersection hit another point exactly, so do not add it again before the hole.
      reIndexedHole = [...reIndexedHole, {
        ...intersection.point
      }];
    }
    poly.splice(intersection.index + 1, 0, ...reIndexedHole);
    holesWithBbox.splice(holeIndex, 1);
  }
  if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
    checkDuplicateReferences(poly);
  }
}

/**
 * Marks non-convex points in a polygon.
 * @param {Vector[]} poly A closed, simple polygon
 * @param {*} direction The direction the polygon is traversed. 'cw' or 'ccw'
 * @returns {number} The number of non-convex points found.
 */
function markReflexPoints(poly, direction) {
  if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
    checkDuplicateReferences(poly);
  }
  if (direction !== 'cw' && direction !== 'ccw') {
    throw new Error('direction must be either "cw" or "ccw"');
  }
  let nReflexPoints = 0;
  for (let i = 0; i < poly.length; i++) {
    let ip1 = (i + 1) % poly.length;
    let im1 = (i - 1 + poly.length) % poly.length;
    let d = (0, _vector.cross)((0, _vector.sub)(poly[i], poly[im1]), (0, _vector.sub)(poly[ip1], poly[i]));
    if (d > 0 && direction === 'cw' || d < 0 && direction === 'ccw') {
      poly[i].reflex = true;
      nReflexPoints++;
    } else {
      delete poly[i].reflex;
    }
  }
  return nReflexPoints;
}

/**
 * Alter the traversal direction of a polygon in place.
 * @param {Vector[]} poly A closed, simple polygon.
 * @param {string} direction The direction the poly should be traversed: 'cw' or 'ccw'.
 * @returns {boolean} True if the polygon was reversed, false if it was unchanged.
 */
function alterPolyTraversalDirection(poly, direction) {
  if (direction !== 'cw' && direction !== 'ccw') {
    throw new Error('direction must be either "cw" or "ccw"');
  }
  // Area and direction
  let area = getPolyArea(poly);
  if (area < 0 && direction === 'ccw' || area > 0 && direction === 'cw') {
    poly.reverse();
    return true;
  }
  return false;
}
function getPolyArea(poly) {
  // Area
  let area = 0;
  for (let i = 0; i < poly.length; i++) {
    let ip1 = (i + 1) % poly.length;
    area += poly[i].x * poly[ip1].y - poly[ip1].x * poly[i].y;
  }
  return area * 0.5;
}

/**
 * Returns an angle bisector of the polygon at the specified vertex, such that the direction of the angle bisector is
 * pointing away from the two adjoining segments.
 * @param poly A closed, simple polygon. 
 * @param index The index of the point to get the angle bisector at.
 * @returns The angle bisector of the polygon at the specified index.
 */
function getAngleBisector(poly, index) {
  let a = poly[(index - 1 + poly.length) % poly.length];
  let b = poly[index];
  let c = poly[(index + 1) % poly.length];
  let ab = (0, _vector.sub)(b, a);
  let cb = (0, _vector.sub)(b, c);
  let normab = (0, _vector.normalize)(ab);
  let normcb = (0, _vector.normalize)(cb);
  let bisector = (0, _vector.normalize)((0, _vector.add)(normab, normcb));
  return {
    point: b,
    direction: bisector
  };
}

/**
 * Cast a ray toward a polygon and return the line segment of the polygon that was hit and the parametric coordinate
 * along that line of the intersection.
 * @param {Vector[]} poly A closed, simple polygon.
 * @param {Vector} point The origin of the ray.
 * @param {Vector} direction The direction of the ray.
 * @param {string} enforcePolyDirection If set, only intersects line segments traversed in the given direction (relative to the ray origin).
 * @param {number} excludeIndex If a non negative number, the index of a vertex of the polygon to exclude from the from
 * test. Default = -1, meaning no vertices are excluded.
 * @param {number} tolerance The tolerance for considering whether the intersection point lands on a vertex.
 * @returns {{index: number, t: number}} The index of the polygon segment that was hit and the parametric coordinate.
 */
function rayCast(poly, point, direction, enforcePolyDirection = null, excludeIndex = -1, tolerance = 1e-8) {
  if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
    checkDuplicateReferences(poly);
  }
  let excludeIndicies = new Uint8Array(poly.length);
  let dots = new Float64Array(poly.length);
  let crosses = new Float64Array(poly.length);
  if (excludeIndex >= 0) {
    excludeIndicies[excludeIndex] = 1;
  }
  let intersections = [];

  // First test for hits directly on points
  for (let k = 0; k < poly.length; k++) {
    if (excludeIndicies[k] === 1) {
      continue;
    }
    let p1 = (0, _vector.sub)(poly[k], point); // vector from the ray origin to the point
    dots[k] = (0, _vector.dot)(p1, direction); // positive if the point is in the direction of the ray
    crosses[k] = (0, _vector.cross)(p1, direction); // 0 means the point is on the ray
    // Does the ray intersect point p1?
    if (Math.abs(crosses[k]) < tolerance && dots[k] > 0) {
      // Do not test this point again
      excludeIndicies[k] = 1;

      // Inspect this point's neighbours to see if the ray is traversing the polygon in the correct direction
      let k0 = (k - 1 + poly.length) % poly.length;
      let p0 = (0, _vector.sub)(poly[k0], point);
      let cross0 = (0, _vector.cross)(p0, direction);
      let k2 = (k + 1) % poly.length;
      let p2 = (0, _vector.sub)(poly[k2], point);
      let cross2 = (0, _vector.cross)(p2, direction);

      // Of the previous and next segments, if the closest of those two is traversed in the required direction, or if
      // the direction is not enforced, then add the intersection.

      // Meaning of cross wrt traversal direction:
      // cross > 0 means the point is to the right of the ray
      // cross < 0 means the point is to the left of the ray

      // The "closest" segment is the one with the smallest dot product with the ray direction. This is the segment the
      // ray hits "first". In the event of a tie, either one can be hit.
      let dir0 = (0, _vector.normalize)((0, _vector.sub)(poly[k0], poly[k]));
      let dir2 = (0, _vector.normalize)((0, _vector.sub)(poly[k2], poly[k]));
      let dot0 = (0, _vector.dot)(dir0, direction);
      let dot2 = (0, _vector.dot)(dir2, direction);
      if (enforcePolyDirection === 'cw' && (cross0 < 0 && dot0 <= dot2 || cross2 > 0 && dot2 <= dot0) || enforcePolyDirection === 'ccw' && (cross0 > 0 && dot0 <= dot2 || cross2 < 0 && dot2 <= dot0) || enforcePolyDirection === null) {
        // Add the point
        intersections.push({
          index: k,
          t: 0,
          distance: (0, _vector.norm)(p1),
          point: (0, _vector.add)(p1, point)
        });
      }
    }
  }

  // Now check for intersections with line segments
  for (let k1 = 0; k1 < poly.length; k1++) {
    let k2 = (k1 + 1) % poly.length;

    // Don't test line segments if either point was hit or is excluded
    if (excludeIndicies[k1] === 1 || excludeIndicies[k2] === 1) {
      continue;
    }

    // Does the ray intersect the line segment between this point and the previous point?
    if (dots[k1] > 0 || dots[k2] > 0) {
      // At least one point must be in the direction of the ray
      let cross1 = crosses[k1];
      let cross2 = crosses[k2];
      if (cross1 * cross2 <= 0) {
        // The line segment crosses the ray (maybe).

        // cross > 0 means the point is to the right of the ray
        // cross < 0 means the point is to the left of the ray
        if (enforcePolyDirection === 'cw' && cross1 < 0 && cross2 > 0 || enforcePolyDirection === 'ccw' && cross1 > 0 && cross2 < 0 || enforcePolyDirection == null) {
          let p1 = (0, _vector.sub)(poly[k1], point);
          let p2 = (0, _vector.sub)(poly[k2], point);
          const x1 = 0;
          const x2 = direction.x;
          const x3 = p1.x;
          const x4 = p2.x;
          const y1 = 0;
          const y2 = direction.y;
          const y3 = p1.y;
          const y4 = p2.y;
          const den = (x4 - x3) * (y1 - y2) - (x1 - x2) * (y4 - y3);
          const taNum = (y3 - y4) * (x1 - x3) + (x4 - x3) * (y1 - y3);
          const tbNum = (y1 - y2) * (x1 - x3) + (x2 - x1) * (y1 - y3);
          const ta = taNum / den;
          const tb = tbNum / den;
          if (ta > 0) {
            intersections.push({
              index: k1,
              t: tb,
              distance: ta,
              point: (0, _vector.add)((0, _vector.mul)(direction, ta), point)
            });
          }
        }
      }
    }
  }

  // Return only the closest intersection
  if (intersections.length > 0) {
    let closestIntersection = intersections[0];
    let closestDistance = intersections[0].distance;
    for (let i = 0; i < intersections.length; i++) {
      if (intersections[i].distance < closestDistance) {
        closestIntersection = intersections[i];
        closestDistance = intersections[i].distance;
      }
    }
    return closestIntersection;
  } else {
    return null;
  }
}
let svg = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" preserveAspectRatio="xMinYMin meet">\n`;
let svgOffset = {
  x: 0,
  y: 0
};
let defaultSvgOffset = {
  x: 0,
  y: 0
};
function setSvgOffset(offset) {
  defaultSvgOffset = offset;
}
function debugSvg(polys, holes = []) {
  svgOffset = (0, _vector.add)(svgOffset, defaultSvgOffset);
  svg += `<g transform="matrix(1,0,${svgOffset.x},-1,0,${svgOffset.y})">\n`;
  for (let poly of polys) {
    svg += `<path fill="none" stroke="#0000ff" stroke-width="0.1%" d="${createContourSvgString(poly)}"/>\n`;
  }
  if (holes) {
    for (let hole of holes) {
      svg += `<path fill="none" stroke="#00ff00" stroke-width="0.1%" d="${createContourSvgString(hole)}"/>\n`;
    }
  }
  svg += `</g>\n`;
}
function createContourSvgString(poly) {
  let svg = '';
  for (let i = 0; i < poly.length + 1; i++) {
    if (i === 0) svg += `M${poly[i % poly.length].x} ${poly[i % poly.length].y}`;else svg += `L${poly[i % poly.length].x} ${poly[i % poly.length].y}`;
  }
  return svg;
}
function saveSvg(name = 'convexDecomposition_debug.svg') {
  svg += '</svg>\n';
  const fs = require('fs');
  if (typeof fs.writeFileSync === 'function') {
    fs.writeFileSync(name, svg);
  } else {
    console.log(svg);
  }
}
function resetSvg() {
  svg = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" preserveAspectRatio="xMinYMin meet">\n`;
  svgOffset = {
    x: 0,
    y: 0
  };
  defaultSvgOffset = {
    x: 0,
    y: 0
  };
}
function checkDuplicateReferences(...poly) {
  for (let i = 0; i < poly.length - 1; i++) {
    for (let j = i + 1; j < poly.length; j++) {
      if (poly[i] === poly[j]) {
        throw new Error(`Error: Duplicate reference: poly[${i}] === poly[${j}]`);
      }
      for (let k = 0; k < poly[i].length; k++) {
        for (let l = 0; l < poly[j].length; l++) {
          if (poly[i][k] === poly[j][l]) {
            throw new Error(`Error: Duplicate reference: poly[${i}][${k}] === poly[j][${l}]`);
          }
        }
      }
    }
  }
}